import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  Award,
  BookOpen,
  CalendarDays,
  ChevronRight,
  Layers,
  RefreshCw,
  UserCheck,
  UserMinus,
  UserPlus,
  Users,
} from "lucide-react";
import { getElearningDashboard } from "../../api/elearning/dashboard";
import { getAttendanceHistory } from "../../api/elearning/attendance/attendance";
import { getCertificates } from "../../api/elearning/certificates/certificates";
import { hasPermission } from "../../utils/permissionUtils";
import {
  filterHubShortcuts,
  getElearningHubItems,
  HUB_PREVIEW_LIMIT,
} from "./mobileNavConfig";
import {
  MobilePageShell,
  MobilePageHeader,
  MobileSectionHeader,
  MobileStatCard,
  MobileShortcutCard,
} from "./MobileNavPrimitives";

const SHORTCUT_TONES = {
  edu_documents: "sky",
  subjects: "violet",
  lessons: "amber",
  results: "emerald",
  calendar: "rose",
  certificates: "indigo",
  students: "sky",
  classes: "emerald",
  courses: "indigo",
  edu_attendance: "teal",
};

const KPI_KEY_BY_CARD = {
  students: "students",
  classes: "classes",
  courses: "courses",
  attendance: "edu_attendance",
};

const ATTENDANCE_STATUS_LABEL = {
  Absent: "Vắng",
  Late: "Muộn",
  absent: "Vắng",
  late: "Muộn",
};

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}%`;
}

function fmtDateTime(value) {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm", { locale: vi });
  } catch {
    return "—";
  }
}

function fmtDate(value) {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM/yyyy", { locale: vi });
  } catch {
    return "—";
  }
}

function JournalSection({ title, tone, action, children }) {
  return (
    <section>
      <MobileSectionHeader title={title} tone={tone} action={action} />
      {children}
    </section>
  );
}

function JournalList({ items, emptyText, renderItem }) {
  if (!items.length) {
    return (
      <p className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-[12px] text-gray-400">
        {emptyText}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm divide-y divide-gray-50">
      {items.map(renderItem)}
    </div>
  );
}

export default function ElearningHubScreen() {
  const navigate = useNavigate();
  const hubItems = useMemo(() => getElearningHubItems(), []);

  const canDash = hasPermission("elearning.dashboard", "R");
  const canStudents = hasPermission("elearning.students", "R");
  const canClasses = hasPermission("elearning.classes", "R");
  const canCourses = hasPermission("elearning.courses", "R");
  const canAttendance = hasPermission("elearning.attendance", "R");
  const canCalendar = hasPermission("elearning.calendar", "R");
  const canCertificates = hasPermission("elearning.certificates", "R");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [scheduleToday, setScheduleToday] = useState([]);
  const [recentEnrollments, setRecentEnrollments] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [recentCertificates, setRecentCertificates] = useState([]);

  const load = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      let nextStats = null;
      let nextSchedule = [];
      let nextEnrollments = [];
      let nextAttendance = [];
      let nextCertificates = [];
      const jobs = [];

      if (canDash) {
        jobs.push(
          getElearningDashboard({
            year: new Date().getFullYear(),
            branch_id: "all",
          })
            .then((data) => {
              nextStats = data?.stats || null;
              nextSchedule = Array.isArray(data?.schedule_today)
                ? data.schedule_today.slice(0, HUB_PREVIEW_LIMIT)
                : [];
              nextEnrollments = Array.isArray(data?.recent_enrollments)
                ? data.recent_enrollments.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      }

      if (canAttendance) {
        jobs.push(
          getAttendanceHistory(
            { page: 1, size: HUB_PREVIEW_LIMIT },
            { silent: true }
          )
            .then((data) => {
              nextAttendance = Array.isArray(data?.records)
                ? data.records.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      }

      if (canCertificates) {
        jobs.push(
          getCertificates({ page: 1, size: HUB_PREVIEW_LIMIT })
            .then((data) => {
              nextCertificates = Array.isArray(data?.records)
                ? data.records.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      }

      await Promise.all(jobs);
      setStats(nextStats);
      setScheduleToday(nextSchedule);
      setRecentEnrollments(nextEnrollments);
      setRecentAttendance(nextAttendance);
      setRecentCertificates(nextCertificates);
      setLoading(false);
      setRefreshing(false);
    },
    [canDash, canAttendance, canCertificates]
  );

  useEffect(() => {
    load();
  }, [load]);

  const kpiCards = useMemo(() => {
    const cards = [];
    const s = stats;
    if (!s) return cards;

    if (canStudents) {
      cards.push({
        key: "students",
        label: "Học viên",
        value: String(s.students_active ?? "—"),
        hint: "Đang theo lớp",
        icon: Users,
        tone: "sky",
        href: "/elearning/students",
      });
    }
    if (canClasses) {
      cards.push({
        key: "classes",
        label: "Lớp học",
        value: String(s.classes_active ?? "—"),
        hint: "Trong phạm vi",
        icon: Layers,
        tone: "emerald",
        href: "/elearning/classes",
      });
    }
    if (canCourses) {
      cards.push({
        key: "courses",
        label: "Khóa học",
        value: String(s.courses_open ?? "—"),
        hint: "Có lớp mở",
        icon: BookOpen,
        tone: "indigo",
        href: "/elearning/courses",
      });
    }
    if (canAttendance) {
      cards.push({
        key: "attendance",
        label: "Điểm danh",
        value: fmtPct(s.attendance_rate_pct),
        hint: "Tỉ lệ trong phạm vi",
        icon: UserCheck,
        tone: "teal",
        href: "/elearning/edu_attendance",
      });
    }

    return cards.slice(0, 4);
  }, [stats, canStudents, canClasses, canCourses, canAttendance]);

  const shortcuts = useMemo(() => {
    const exclude = kpiCards
      .map((c) => KPI_KEY_BY_CARD[c.key])
      .filter(Boolean);
    return filterHubShortcuts(hubItems, exclude);
  }, [hubItems, kpiCards]);

  const showJournalBlock =
    canDash || canAttendance || canCertificates;

  const hasJournalContent =
    recentEnrollments.length > 0 ||
    recentAttendance.length > 0 ||
    recentCertificates.length > 0;

  return (
    <MobilePageShell>
      <MobilePageHeader
        title="E-Learning"
        subtitle="Khóa học, lớp, học viên…"
        tone="indigo"
        trailing={
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-indigo-100 bg-indigo-50 text-indigo-700 active:bg-indigo-100 disabled:opacity-50"
            aria-label="Làm mới"
          >
            <RefreshCw
              size={16}
              className={refreshing ? "animate-spin" : undefined}
            />
          </button>
        }
      />

      <div className="space-y-5 px-5 pt-5 pb-4">
        {(canDash || kpiCards.length > 0) && (
          <section>
            <MobileSectionHeader title="Tình hình E-Learning" tone="indigo" />
            {loading ? (
              <div className="grid grid-cols-2 gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-[100px] animate-pulse rounded-2xl bg-indigo-100/50"
                  />
                ))}
              </div>
            ) : kpiCards.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {kpiCards.map((card) => (
                  <MobileStatCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    hint={card.hint}
                    icon={card.icon}
                    tone={card.tone}
                    onClick={() => navigate(card.href)}
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/40 px-4 py-8 text-center text-[12px] text-gray-400">
                Chưa có chỉ số E-Learning
              </p>
            )}
          </section>
        )}

        {!loading && canDash && scheduleToday.length > 0 ? (
          <JournalSection
            title="Cần bạn xử lý"
            tone="rose"
            action={
              canCalendar ? (
                <button
                  type="button"
                  onClick={() => navigate("/elearning/calendar")}
                  className="text-[12px] font-medium text-rose-700"
                >
                  TKB
                </button>
              ) : null
            }
          >
            <JournalList
              items={scheduleToday}
              emptyText="Hôm nay không có buổi học theo lịch lớp"
              renderItem={(row, idx) => (
                <button
                  key={`${row.class_id || idx}-${row.time || idx}`}
                  type="button"
                  onClick={() =>
                    navigate(
                      canCalendar
                        ? "/elearning/calendar"
                        : "/elearning/classes"
                    )
                  }
                  className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                >
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
                    <CalendarDays size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-800">
                      {row.title || row.course || "Buổi học"}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] text-gray-400">
                      {[row.time, row.room, row.course_name || row.course]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <ChevronRight
                    size={14}
                    className="mt-2 shrink-0 text-gray-300"
                  />
                </button>
              )}
            />
          </JournalSection>
        ) : null}

        {shortcuts.length > 0 ? (
          <section>
            <MobileSectionHeader title="Lối tắt" tone="violet" />
            <div className="grid grid-cols-2 gap-3">
              {shortcuts.map((item) => (
                <MobileShortcutCard
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  tone={SHORTCUT_TONES[item.key] || "indigo"}
                  onClick={() => navigate(`/elearning/${item.key}`)}
                />
              ))}
            </div>
          </section>
        ) : null}

        {showJournalBlock ? (
          <section className="space-y-5">
            <MobileSectionHeader title="Nhật ký gần nhất" tone="amber" />
            <p className="-mt-1 pl-3 text-[11px] text-gray-500">
              Đọc nhanh tình hình đào tạo theo quyền của bạn
            </p>

            {loading ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-28 animate-pulse rounded-2xl bg-amber-100/40"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-5">
                {canDash ? (
                  <JournalSection
                    title="Nhật ký đăng ký gần nhất"
                    tone="sky"
                    action={
                      canStudents ? (
                        <button
                          type="button"
                          onClick={() => navigate("/elearning/students")}
                          className="text-[12px] font-medium text-sky-700"
                        >
                          Học viên
                        </button>
                      ) : null
                    }
                  >
                    <JournalList
                      items={recentEnrollments}
                      emptyText="Chưa có đăng ký lớp gần đây"
                      renderItem={(row, idx) => (
                        <button
                          key={`${row.student_name}-${row.enrolled_at}-${idx}`}
                          type="button"
                          onClick={() =>
                            navigate(
                              canStudents
                                ? "/elearning/students"
                                : "/elearning/classes"
                            )
                          }
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700">
                            <UserPlus size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {row.student_name || "Học viên"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[row.class_name, row.course_name]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {fmtDateTime(row.enrolled_at)}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {canAttendance ? (
                  <JournalSection
                    title="Nhật ký vắng/muộn gần nhất"
                    tone="teal"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/elearning/edu_attendance")}
                        className="text-[12px] font-medium text-teal-700"
                      >
                        Điểm danh
                      </button>
                    }
                  >
                    <JournalList
                      items={recentAttendance}
                      emptyText="Không có bản ghi vắng hoặc muộn gần đây"
                      renderItem={(row) => (
                        <button
                          key={row.attendance_id || `${row.student_id}-${row.date}`}
                          type="button"
                          onClick={() => navigate("/elearning/edu_attendance")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                            <UserMinus size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {row.student_name || "Học viên"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                row.class_name,
                                ATTENDANCE_STATUS_LABEL[row.status] || row.status,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {fmtDate(row.date)}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {canCertificates ? (
                  <JournalSection
                    title="Nhật ký cấp chứng nhận gần nhất"
                    tone="indigo"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/elearning/certificates")}
                        className="text-[12px] font-medium text-indigo-700"
                      >
                        Xem thêm
                      </button>
                    }
                  >
                    <JournalList
                      items={recentCertificates}
                      emptyText="Chưa có chứng nhận được cấp gần đây"
                      renderItem={(row) => (
                        <button
                          key={row.id}
                          type="button"
                          onClick={() => navigate("/elearning/certificates")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                            <Award size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {row.recipient_name || "Người nhận"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                row.course_name || row.subject_name,
                                row.cert_id_display || row.cert_id,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {fmtDate(row.issue_date || row.created_at)}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {!hasJournalContent ? (
                  <p className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/40 px-4 py-8 text-center text-[12px] text-gray-400">
                    Chưa có nhật ký để hiển thị theo quyền hiện tại
                  </p>
                ) : null}
              </div>
            )}
          </section>
        ) : null}

        {!loading &&
        kpiCards.length === 0 &&
        shortcuts.length === 0 &&
        !showJournalBlock ? (
          <p className="py-12 text-center text-[12px] text-gray-400">
            Không có chức năng E-Learning khả dụng
          </p>
        ) : null}
      </div>
    </MobilePageShell>
  );
}
